import React, { useMemo } from "react";

import Svg, { Path } from "react-native-svg";
import { useTheme } from "./AHTheme/AHTheme";
import { useScaleFactor } from "./AHScaleFactor";
import { useCircleSize } from "./LayoutProvider";
import { Box } from "./AHTheme/Box";
import { AHText } from "./Text/AHText";

const GAP_ANGLE = 10;
export function SubTodoIndicator(props: { statuses: ("DONE" | "PENDING")[] }) {
  const completed = props.statuses.filter((status) => status === "DONE").length;
  const total = props.statuses.length;
  const theme = useTheme();
  const sf = useScaleFactor();
  const size = useCircleSize("small");

  const strokeWidth = Math.round(2 * sf);
  const radius = (size - strokeWidth) / 2;

  const statusComponents = useMemo(() => {
    return props.statuses.map(
      (status, index) => {
        const anglePerSegment = 360 / total;
        const gapAngle = GAP_ANGLE; // Small gap between segments in degrees
        const arcAngle = anglePerSegment - gapAngle;

        // Calculate the start angle for this segment (starting from top, going clockwise)
        const startAngle = -90 + GAP_ANGLE / 2 + index * anglePerSegment;
        const endAngle = startAngle + arcAngle;

        // Convert angles to radians
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;

        // Calculate start and end points
        const startX = size / 2 + radius * Math.cos(startRad);
        const startY = size / 2 + radius * Math.sin(startRad);
        const endX = size / 2 + radius * Math.cos(endRad);
        const endY = size / 2 + radius * Math.sin(endRad);

        // Determine if the arc should be drawn as a large arc
        const largeArcFlag = arcAngle > 180 ? 1 : 0;

        // Create SVG path for the arc
        const pathData = `
        M ${startX} ${startY}
        A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}
      `;

        return (
          <Path
            key={index}
            d={pathData}
            stroke={status === "DONE" ? theme.positive : theme["border-strong"]}
            strokeWidth={strokeWidth}
            fill="transparent"
          />
        );
      },
      [props.statuses]
    );
  }, [props.statuses]);
  if (total === 0) {
    return null;
  }

  return (
    <Box
      spaceHorizontal="small"
      style={{
        flexDirection: "row",
        borderWidth: 1,
        alignItems: "center",
      }}
      borderColor="border-primary"
      spaceVertical="xsmall"
      cornerRadius="full"
    >
      <Svg
        accessibilityRole="progressbar"
        accessibilityLabel={`${completed} out of ${total} subtasks completed`}
        accessibilityValue={{ now: completed, min: 0, max: total }}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        {statusComponents}
      </Svg>
      <AHText selectable={false} spaceLeft="small" size={12} type="detail">
        {`${completed}/${total}`}
      </AHText>
    </Box>
  );
}
